import { useContext } from 'react';

import { faPhoneFlip } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Stack, Typography } from '@mui/material';

import { AnalyticsContext } from 'src/AnalyticsContext';
import ResidentCard from 'src/components/residents/ResidentCard';
import { CheckInStatuses } from 'src/constants/CheckIn';
import useCareZones from 'src/hooks/useCareZones';
import { CheckIn, Resident, Unit } from 'src/store/sageAdminApi';
import { Formats, formatPhoneNumber } from 'src/utils/formUtils';
import { getResidentDisplayName } from 'src/utils/residentUtils';

interface ResidentsInUnitProps {
    analyticsPrefix?: string;
    onResidentClick?: (resident: Resident) => void;
    showEmptyMessage?: boolean;
    unit: Unit & { checkIn?: CheckIn };
}

const ResidentsInUnit = (props: ResidentsInUnitProps) => {
    const { analyticsPrefix, onResidentClick, showEmptyMessage = false, unit } = props;

    const { currentOccupants = [], displayName } = unit;

    const { trackEvent } = useContext(AnalyticsContext);

    const { isCheckInsEnabled } = useCareZones();
    const unitCheckedIn = isCheckInsEnabled && unit?.checkIn?.status === CheckInStatuses.Complete;

    const residentsWithPhoneNumber = currentOccupants.filter((occupant) => occupant.personalPhone);

    return (
        <>
            {currentOccupants.length > 0 && (
                <>
                    <Typography variant="h4" component="p" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Residents
                    </Typography>

                    <Stack sx={{ gap: 2 }}>
                        {currentOccupants.map((occupant, index) => (
                            <ResidentCard
                                index={index}
                                key={occupant.id}
                                unitIsCheckedIn={unitCheckedIn}
                                resident={occupant}
                                onResidentClick={
                                    onResidentClick ? () => onResidentClick(occupant) : () => {}
                                }
                            />
                        ))}
                    </Stack>

                    {(residentsWithPhoneNumber.length > 0 || unit.phoneNumber) && (
                        <Stack sx={{ gap: 2, mt: 2.5 }}>
                            {residentsWithPhoneNumber.map((occupant, index) => (
                                <Button
                                    key={occupant.id}
                                    fullWidth
                                    disableElevation
                                    variant="outlined"
                                    endIcon={<FontAwesomeIcon icon={faPhoneFlip} />}
                                    href={`tel:${occupant.personalPhone}`}
                                    onClick={() =>
                                        trackEvent('Call Resident Button Clicked', {
                                            context: analyticsPrefix,
                                            listIndex: index,
                                        })
                                    }
                                >
                                    <Stack direction="row" sx={{ gap: 0.5 }}>
                                        <span>
                                            Call{' '}
                                            {getResidentDisplayName(occupant, { firstOnly: true })}
                                        </span>
                                        <b>
                                            {formatPhoneNumber(
                                                occupant.personalPhone,
                                                Formats.National
                                            )}
                                        </b>
                                    </Stack>
                                </Button>
                            ))}

                            {unit.phoneNumber && (
                                <Button
                                    fullWidth
                                    disableElevation
                                    variant="outlined"
                                    endIcon={<FontAwesomeIcon icon={faPhoneFlip} />}
                                    href={`tel:${unit.phoneNumber}`}
                                    className="iconRight"
                                    onClick={() =>
                                        trackEvent('Call Unit Button Clicked', {
                                            context: analyticsPrefix,
                                        })
                                    }
                                >
                                    <Stack direction="row" sx={{ gap: 0.5 }}>
                                        <span>Call Unit</span>
                                        <b>
                                            {formatPhoneNumber(unit.phoneNumber, Formats.National)}
                                        </b>
                                    </Stack>
                                </Button>
                            )}
                        </Stack>
                    )}
                </>
            )}
            {showEmptyMessage && currentOccupants.length < 1 && (
                <Typography variant="subtitle1" component="p" sx={{ mb: 1.5, color: 'grey.700' }}>
                    No residents in {displayName ?? 'unit'}
                </Typography>
            )}
        </>
    );
};

export default ResidentsInUnit;
